import * as React from 'react'
import Accordion from '../../components/Accordion/Accordion'
import styled from 'styled-components'
import { Style } from '../../styles'
import { WithTranslation, withTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import EditorContainer from '../../components/Editor/EditorContainer'
import Editor, { MESSAGE_EDITOR_CONFIG } from '../../components/Editor/Editor'
import ResourceCreatablePowerSelect from '../../components/Form/ResourceCreatablePowerSelect'
import CheckboxInput from '../../components/Form/CheckboxInput'
import Button from '../../components/Button/Button'
import ButtonLink from '../../components/Button/ButtonLink'
import PowerSelect from '../../components/Form/PowerSelect'
import WidgetOutboundMessagesController from '../../controllers/WidgetOutboundMessagesController'
import MobilityHelper from '../../helpers/MobilityHelper'
import {
  PublicLocale,
  Widget,
  WidgetOutboundMessage as IWidgetOutboundMessage,
  WidgetOutboundMessageTranslateableFields,
  WidgetOutboundPredicate,
  WidgetOutboundPredicateAttribute,
  WidgetOutboundPredicateComparison,
  WidgetOutboundPredicateGroup,
  WidgetOutboundPredicateGroupType,
  WidgetOutboundPredicateType,
} from '../../types'

type PredicateGroupSource = 'client' | 'regular'
import PageLoader from '../../components/Page/PageLoader'
import Notification from '../../utilities/Notification'

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    padding-bottom: ${Style.spacing.x3};
  }
`

const Rules = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: ${Style.spacing.x2};
`

const RuleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${Style.spacing.x2};
  border: 1px solid grey;
  border-radius: 4px;
`

const RuleGroupHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const RuleGroupType = styled.div`
  min-width: 100px;
  max-width: 100px;
`

const RuleItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  label {
    color: black;
  }

  input {
    max-width: 250px;
  }
`

const RuleItemPredicate = styled.div`
  min-width: 140px;
  max-width: 140px;
`

const ATTRIBUTE_TYPE_MAP: Record<WidgetOutboundPredicateAttribute, WidgetOutboundPredicateType> = {
  [WidgetOutboundPredicateAttribute.CURRENT_URL]: WidgetOutboundPredicateType.STRING,
  [WidgetOutboundPredicateAttribute.TIME_ON_PAGE]: WidgetOutboundPredicateType.DURATION_INTEGER,
  [WidgetOutboundPredicateAttribute.LOCALE]: WidgetOutboundPredicateType.STRING,
}

const DEFAULT_PREDICATE: WidgetOutboundPredicate = {
  attribute: WidgetOutboundPredicateAttribute.CURRENT_URL,
  type: WidgetOutboundPredicateType.STRING,
  comparison: WidgetOutboundPredicateComparison.CONTAINS,
  value: '',
}

interface IProps extends WithTranslation, RouteComponentProps<{ id: string, outboundMessageId: string }> { }
interface IState {
  outboundMessage: IWidgetOutboundMessage
  widget: Widget | null
  activeLocale: PublicLocale | null
  didInitialLoad: boolean
  isSaving: boolean
}

class WidgetOutboundMessage extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      outboundMessage: null,
      widget: null,
      activeLocale: null,
      didInitialLoad: false,
      isSaving: false,
    }
    this.fetchForm = this.fetchForm.bind(this)
    this.onAuthorChange = this.onAuthorChange.bind(this)
    this.onAssigneeChange = this.onAssigneeChange.bind(this)
    this.onLocaleChange = this.onLocaleChange.bind(this)
    this.onMessageChange = this.onMessageChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onAddGroup = this.onAddGroup.bind(this)
    this.onAddPredicateGroup = this.onAddPredicateGroup.bind(this)
  }

  componentDidMount() {
    this.fetchForm()
  }

  async fetchForm() {
    const { match: { params: { id: widgetId, outboundMessageId } } } = this.props

    try {
      const response = await WidgetOutboundMessagesController.getForm(widgetId, outboundMessageId)
      const { outbound_message, widget } = response
      const defaultLocale = widget?.supported_locales?.[0] ?? widget?.locale ?? null

      this.setState({
        outboundMessage: outbound_message || this.state.outboundMessage,
        widget: widget || null,
        activeLocale: defaultLocale,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
      this.setState({ didInitialLoad: true })
    }
  }

  onAuthorChange(authorId: string, author: any) {
    const { outboundMessage } = this.state

    this.setState({
      outboundMessage: {
        ...outboundMessage,
        author: author,
        author_id: authorId
      }
    })
  }

  onAssigneeChange(assigneeId: string, assignee: any) {
    const { outboundMessage } = this.state

    this.setState({
      outboundMessage: {
        ...outboundMessage,
        assignee: assignee,
        assignee_id: assigneeId
      }
    })
  }

  onLocaleChange(option: any) {
    this.setState({ activeLocale: option.value })
  }

  onMessageChange(message: string) {
    const { outboundMessage, activeLocale } = this.state
    if (!activeLocale) return

    const updatedTranslations = MobilityHelper.updateTranslation<WidgetOutboundMessageTranslateableFields>(
      activeLocale, outboundMessage.translations || {}, 'message', message
    )
    this.setState({ outboundMessage: { ...outboundMessage, translations: updatedTranslations } })
  }

  async onSubmit() {
    const { match: { params: { id: widgetId } } } = this.props
    const { outboundMessage } = this.state

    this.setState({ isSaving: true })
    let response = null
    try {
      if (outboundMessage.id) {
        response = await WidgetOutboundMessagesController.update(widgetId, outboundMessage)
      } else {
        response = await WidgetOutboundMessagesController.create(widgetId, outboundMessage)
      }

      if (response?.errors) {
        Notification.notifyError('WidgetOutboundMessage::Failed to save outbound message')
      } else {
        this.setState({ outboundMessage: response })
        Notification.notifySuccess('WidgetOutboundMessage::Outbound message saved successfully')
      }
    } finally {
      this.setState({ isSaving: false })
    }
  }

  private getClientPredicateGroup(): WidgetOutboundPredicateGroup[] {
    return this.state.outboundMessage?.client_predicate_group?.predicates || []
  }

  private setClientPredicateGroups(groups: WidgetOutboundPredicateGroup[]) {
    const { outboundMessage } = this.state

    this.setState({
      outboundMessage: {
        ...outboundMessage,
        client_predicate_group: { predicates: groups },
      },
    })
  }

  onAddGroup() {
    this.setClientPredicateGroups([
      ...this.getClientPredicateGroup(),
      { type: WidgetOutboundPredicateGroupType.AND, predicates: [{ ...DEFAULT_PREDICATE }] },
    ])
  }

  onDeleteGroup(groupIndex: number) {
    const groups = [...this.getClientPredicateGroup()]
    groups.splice(groupIndex, 1)
    this.setClientPredicateGroups(groups)
  }

  onUpdateGroupType(groupIndex: number, type: WidgetOutboundPredicateGroupType) {
    const groups = [...this.getClientPredicateGroup()]
    groups[groupIndex] = { ...groups[groupIndex], type }
    this.setClientPredicateGroups(groups)
  }

  onAddPredicate(groupIndex: number) {
    const groups = [...this.getClientPredicateGroup()]
    groups[groupIndex] = {
      ...groups[groupIndex],
      predicates: [...groups[groupIndex].predicates, { ...DEFAULT_PREDICATE }],
    }
    this.setClientPredicateGroups(groups)
  }

  onDeletePredicate(groupIndex: number, predicateIndex: number) {
    const groups = [...this.getClientPredicateGroup()]
    const predicates = [...groups[groupIndex].predicates]
    predicates.splice(predicateIndex, 1)
    groups[groupIndex] = { ...groups[groupIndex], predicates }
    this.setClientPredicateGroups(groups)
  }

  onUpdatePredicate(groupIndex: number, predicateIndex: number, update: Partial<WidgetOutboundPredicate>) {
    const groups = [...this.getClientPredicateGroup()]
    const predicates = [...groups[groupIndex].predicates]
    let merged = { ...predicates[predicateIndex], ...update }

    if (update.attribute) {
      const newType = ATTRIBUTE_TYPE_MAP[update.attribute]
      const defaultComparisonByType: Record<WidgetOutboundPredicateType, WidgetOutboundPredicateComparison> = {
        [WidgetOutboundPredicateType.STRING]: WidgetOutboundPredicateComparison.CONTAINS,
        [WidgetOutboundPredicateType.DURATION_INTEGER]: WidgetOutboundPredicateComparison.EQ,
      }
      merged = { ...merged, type: newType, comparison: defaultComparisonByType[newType] }
    }

    predicates[predicateIndex] = merged
    groups[groupIndex] = { ...groups[groupIndex], predicates }
    this.setClientPredicateGroups(groups)
  }

  private getPredicateGroups(): WidgetOutboundPredicateGroup[] {
    return this.state.outboundMessage?.predicate_group?.predicates || []
  }

  private setPredicateGroups(groups: WidgetOutboundPredicateGroup[]) {
    const { outboundMessage } = this.state

    this.setState({
      outboundMessage: {
        ...outboundMessage,
        predicate_group: { predicates: groups },
      },
    })
  }

  onAddPredicateGroup() {
    this.setPredicateGroups([
      ...this.getPredicateGroups(),
      {
        type: WidgetOutboundPredicateGroupType.AND,
        predicates: [{ ...DEFAULT_PREDICATE, attribute: WidgetOutboundPredicateAttribute.LOCALE }],
      },
    ])
  }

  onDeletePredicateGroup(groupIndex: number) {
    const groups = [...this.getPredicateGroups()]
    groups.splice(groupIndex, 1)
    this.setPredicateGroups(groups)
  }

  onUpdatePredicateGroupType(groupIndex: number, type: WidgetOutboundPredicateGroupType) {
    const groups = [...this.getPredicateGroups()]
    groups[groupIndex] = { ...groups[groupIndex], type }
    this.setPredicateGroups(groups)
  }

  onAddPredicateGroupPredicate(groupIndex: number) {
    const groups = [...this.getPredicateGroups()]
    groups[groupIndex] = {
      ...groups[groupIndex],
      predicates: [
        ...groups[groupIndex].predicates,
        { ...DEFAULT_PREDICATE, attribute: WidgetOutboundPredicateAttribute.LOCALE },
      ],
    }
    this.setPredicateGroups(groups)
  }

  onDeletePredicateGroupPredicate(groupIndex: number, predicateIndex: number) {
    const groups = [...this.getPredicateGroups()]
    const predicates = [...groups[groupIndex].predicates]
    predicates.splice(predicateIndex, 1)
    groups[groupIndex] = { ...groups[groupIndex], predicates }
    this.setPredicateGroups(groups)
  }

  onUpdatePredicateGroupPredicate(groupIndex: number, predicateIndex: number, update: Partial<WidgetOutboundPredicate>) {
    const groups = [...this.getPredicateGroups()]
    const predicates = [...groups[groupIndex].predicates]
    let merged = { ...predicates[predicateIndex], ...update }

    if (update.attribute) {
      const newType = ATTRIBUTE_TYPE_MAP[update.attribute]
      const defaultComparisonByType: Record<WidgetOutboundPredicateType, WidgetOutboundPredicateComparison> = {
        [WidgetOutboundPredicateType.STRING]: WidgetOutboundPredicateComparison.CONTAINS,
        [WidgetOutboundPredicateType.DURATION_INTEGER]: WidgetOutboundPredicateComparison.EQ,
      }
      merged = { ...merged, type: newType, comparison: defaultComparisonByType[newType] }
    }

    predicates[predicateIndex] = merged
    groups[groupIndex] = { ...groups[groupIndex], predicates }
    this.setPredicateGroups(groups)
  }

  renderPredicateRow(predicate: WidgetOutboundPredicate, groupIndex: number, predicateIndex: number, source: PredicateGroupSource) {
    const { t } = this.props

    const clientAttributeOptions = [
      { label: t('WidgetOutboundMessage::Current URL'), value: WidgetOutboundPredicateAttribute.CURRENT_URL },
      { label: t('WidgetOutboundMessage::Time on page'), value: WidgetOutboundPredicateAttribute.TIME_ON_PAGE },
    ]
    const regularAttributeOptions = [
      { label: t('WidgetOutboundMessage::Locale'), value: WidgetOutboundPredicateAttribute.LOCALE },
    ]
    const attributeOptions = source === 'client' ? clientAttributeOptions : regularAttributeOptions

    const comparisonOptionsByType: Record<WidgetOutboundPredicateType, { label: string; value: WidgetOutboundPredicateComparison }[]> = {
      [WidgetOutboundPredicateType.STRING]: [
        { label: t('WidgetOutboundMessage::contains'), value: WidgetOutboundPredicateComparison.CONTAINS },
        { label: t('WidgetOutboundMessage::does_not_contain'), value: WidgetOutboundPredicateComparison.DOES_NOT_CONTAIN },
        { label: t('WidgetOutboundMessage::is'), value: WidgetOutboundPredicateComparison.IS },
        { label: t('WidgetOutboundMessage::is_not'), value: WidgetOutboundPredicateComparison.IS_NOT },
        { label: t('WidgetOutboundMessage::starts_with'), value: WidgetOutboundPredicateComparison.STARTS_WITH },
        { label: t('WidgetOutboundMessage::ends_with'), value: WidgetOutboundPredicateComparison.ENDS_WITH },
      ],
      [WidgetOutboundPredicateType.DURATION_INTEGER]: [
        { label: t('WidgetOutboundMessage::eq'), value: WidgetOutboundPredicateComparison.EQ },
        { label: t('WidgetOutboundMessage::gt'), value: WidgetOutboundPredicateComparison.GT },
        { label: t('WidgetOutboundMessage::lt'), value: WidgetOutboundPredicateComparison.LT },
        { label: t('WidgetOutboundMessage::gte'), value: WidgetOutboundPredicateComparison.GTE },
        { label: t('WidgetOutboundMessage::lte'), value: WidgetOutboundPredicateComparison.LTE },
      ],
    }

    const comparisonOptions = comparisonOptionsByType[predicate.type]

    return (
      <RuleItem key={predicateIndex}>
        <RuleItemPredicate style={{ minWidth: 250 }}>
          <PowerSelect
            options={attributeOptions}
            value={attributeOptions.find(o => o.value === predicate.attribute) || null}
            onChange={(option: any) => source === 'client'
              ? this.onUpdatePredicate(groupIndex, predicateIndex, { attribute: option?.value })
              : this.onUpdatePredicateGroupPredicate(groupIndex, predicateIndex, { attribute: option?.value })
            }
          />
        </RuleItemPredicate>
        <RuleItemPredicate>
          <PowerSelect
            options={comparisonOptions}
            value={comparisonOptions.find(o => o.value === predicate.comparison) || null}
            onChange={(option: any) => source === 'client'
              ? this.onUpdatePredicate(groupIndex, predicateIndex, { comparison: option?.value })
              : this.onUpdatePredicateGroupPredicate(groupIndex, predicateIndex, { comparison: option?.value })
            }
          />
        </RuleItemPredicate>
        <input
          type={predicate.type === WidgetOutboundPredicateType.DURATION_INTEGER ? 'number' : 'text'}
          value={predicate.value}
          placeholder={predicate.type === WidgetOutboundPredicateType.DURATION_INTEGER
            ? t('WidgetOutboundMessage::Seconds')
            : 'https://example.com/checkout'
          }
          onChange={(e) => source === 'client'
            ? this.onUpdatePredicate(groupIndex, predicateIndex, { value: e.currentTarget.value })
            : this.onUpdatePredicateGroupPredicate(groupIndex, predicateIndex, { value: e.currentTarget.value })
          }
        />
        <ButtonLink
          icon='trash'
          text=''
          onClick={() => source === 'client'
            ? this.onDeletePredicate(groupIndex, predicateIndex)
            : this.onDeletePredicateGroupPredicate(groupIndex, predicateIndex)
          }
        />
      </RuleItem>
    )
  }

  renderPredicateGroup(group: WidgetOutboundPredicateGroup, groupIndex: number, source: PredicateGroupSource) {
    const { t } = this.props

    const groupTypeOptions = [
      { label: t('WidgetOutboundMessage::All rules must match'), value: WidgetOutboundPredicateGroupType.AND },
      { label: t('WidgetOutboundMessage::Any rule can match'), value: WidgetOutboundPredicateGroupType.OR },
    ]

    return (
      <RuleGroup key={groupIndex}>
        <RuleGroupHeader>
          <RuleGroupType style={{ minWidth: 250 }}>
            <PowerSelect
              options={groupTypeOptions}
              value={groupTypeOptions.find(o => o.value === group.type) || null}
              onChange={(option: any) => source === 'client'
                ? this.onUpdateGroupType(groupIndex, option?.value)
                : this.onUpdatePredicateGroupType(groupIndex, option?.value)
              }
            />
          </RuleGroupType>
          <ButtonLink
            icon='trash'
            text=''
            onClick={() => source === 'client'
              ? this.onDeleteGroup(groupIndex)
              : this.onDeletePredicateGroup(groupIndex)
            }
          />
        </RuleGroupHeader>
        {group?.predicates.map((predicate, predicateIndex) =>
          this.renderPredicateRow(predicate, groupIndex, predicateIndex, source)
        )}
        <ButtonLink
          icon='plus'
          text={t('WidgetOutboundMessage::Add condition')}
          onClick={() => source === 'client'
            ? this.onAddPredicate(groupIndex)
            : this.onAddPredicateGroupPredicate(groupIndex)
          }
        />
      </RuleGroup>
    )
  }

  render() {
    const { t } = this.props
    const { didInitialLoad, outboundMessage, widget, activeLocale, isSaving } = this.state

    if (!didInitialLoad) return <PageLoader />

    const predicateGroups = this.getPredicateGroups()
    const clientpredicategroup = this.getClientPredicateGroup()
    const localeOptions = (widget?.supported_locales || []).map(locale => ({
      label: t(`PublicLocale::${locale}`),
      value: locale,
    }))
    const selectedLocaleOption = localeOptions.find(o => o.value === activeLocale) || null
    const message = MobilityHelper.getTranslation<WidgetOutboundMessageTranslateableFields>(
      activeLocale, outboundMessage?.translations || {}, 'message'
    )

    return (
      <AccordionContainer>
        <Accordion title={t('WidgetOutboundMessage::Content')}>
          <div className='form-item'>
            <label>{t('WidgetOutboundMessage::Name')}</label>
            <input
              type='text'
              value={outboundMessage?.name || ''}
              onChange={(e) => this.setState({ outboundMessage: { ...outboundMessage, name: e.currentTarget.value } })}
              placeholder={t('WidgetOutboundMessage::Enter a name')}
            />
          </div>

          <div className='form-item'>
            <label>{t('WidgetOutboundMessage::From')}</label>
            <ResourceCreatablePowerSelect
              type='user'
              value={outboundMessage.author_id || ''}
              onChange={this.onAuthorChange}
              isClearable={false}
              isValidNewOption={() => false}
              isMulti={false}
              placeholder={t('WidgetOutboundMessage::Select assignees')}
            />
          </div>

          <div className='form-item'>
            <label>{t('WidgetOutboundMessage::Assign replies to')}</label>
            <ResourceCreatablePowerSelect
              type='user'
              value={outboundMessage.assignee_id || ''}
              onChange={this.onAssigneeChange}
              isClearable={false}
              isValidNewOption={() => false}
              isMulti={false}
              placeholder={t('WidgetOutboundMessage::Select assignees')}
            />
          </div>
          {localeOptions.length > 1 && (
            <div className='form-item'>
              <label>{t('WidgetOutboundMessage::Language')}</label>
              <PowerSelect
                options={localeOptions}
                value={selectedLocaleOption}
                onChange={this.onLocaleChange}
              />
            </div>
          )}
          <EditorContainer toolbarBottom>
            <Editor
              model={message}
              onModelChange={this.onMessageChange}
              config={{
                ...MESSAGE_EDITOR_CONFIG,
                toolbarBottom: true,
                placeholderText: t('WidgetOutboundMessage::Add a message'),
                heightMin: 200,
                heightMax: 400
              }}
            />
          </EditorContainer>
        </Accordion>
        <Accordion title={t('WidgetOutboundMessage::Rules')}>
          <div className='form-item'>
            <label>{t('WidgetOutboundMessage::Where to send')}</label>

            {clientpredicategroup.length > 0 && <Rules>
              {clientpredicategroup.map((group, groupIndex) => this.renderPredicateGroup(group, groupIndex, 'client'))}
            </Rules>}

            <ButtonLink
              icon='plus'
              text={t('WidgetOutboundMessage::Add rule group')}
              onClick={this.onAddGroup}
            />
          </div>

          <div className='form-item'>
            <label>{t('WidgetOutboundMessage::Additional conditions')}</label>

            {predicateGroups.length > 0 && <Rules>
              {predicateGroups.map((group, groupIndex) => this.renderPredicateGroup(group, groupIndex, 'regular'))}
            </Rules>}

            <ButtonLink
              icon='plus'
              text={t('WidgetOutboundMessage::Add rule group')}
              onClick={this.onAddPredicateGroup}
            />
          </div>
        </Accordion>
        <Accordion title={t('WidgetOutboundMessage::Frequency and scheduling')}>
          <div className='grid'>
            <div className='grid-cell with-4col'>
              <CheckboxInput
                label={t('WidgetOutboundMessage::Send immediately')}
                checked={true}
                onChange={() => { }}
              />
            </div>
          </div>
        </Accordion>
        <Button
          type='primary'
          text={t('WidgetOutboundMessage::Save')}
          isLoading={isSaving}
          onClick={this.onSubmit}
        />
      </AccordionContainer>
    )
  }
}

export default withRouter(withTranslation()(WidgetOutboundMessage))
